(ns hai.tasks.drawer.data
  "Each task drawer's state in app-db, under the drawer's key — `[:task uuid]`
   for a saved task's, `[:draft id]` for a new one's — and the events its
   controls send ([[hai.tasks.drawer.core]]). A drawer's is forgotten when it
   goes, so a task opened again shows its drawer as it first does. Its two
   searches send their requests from here."
  (:require
   [clojure.string :as str]
   [hai.contacts.helpers :as contacts]
   [hai.tasks.drawer.core :as core]
   [hai.ui.rf-sugar :refer [defevent defevent-fx defsub]]
   [hai.ui.viewport :as viewport]))

(def ^:private search-debounce-ms 250)

(defn- initial
  "The drawer on `drawer-key` as it first shows in the layout `db` is in."
  [db drawer-key]
  (core/initial drawer-key (viewport/mobile? db)))

(defn- drawer-in
  [db drawer-key]
  (or (get-in db [::drawers drawer-key])
      (initial db drawer-key)))

(defn- change
  "The drawer on `drawer-key` through `f`."
  [db drawer-key f & args]
  (assoc-in db [::drawers drawer-key] (apply f (drawer-in db drawer-key) args)))

(defn- change-search
  "The drawer's search under `search-key` (`:related-search` or
   `:share-search`) through `f`."
  [db drawer-key search-key f & args]
  (change db drawer-key #(apply update % search-key f args)))

(defn- held?
  "Whether the drawer on `drawer-key` is still out: an answer, or a field
   going away, lands after its drawer has been forgotten."
  [db drawer-key]
  (contains? (::drawers db) drawer-key))

(defn- stop-search-fx
  [drawer-key search-key]
  [:debounce/stop {:id [::search drawer-key search-key]}])

;; Its tray, tab and the search that is out — not what is typed, so typing
;; doesn't redraw the drawer.
(defsub ::drawer [db drawer-key]
  (select-keys (drawer-in db drawer-key) [:open? :tab :search]))

(defsub ::search [db drawer-key search-key]
  (get (drawer-in db drawer-key) search-key))

;;
;; The drawer
;;

(defevent ::press-tab [db drawer-key tab]
  (change db drawer-key core/press-tab tab))

(defevent ::press-add [db drawer-key tab]
  (change db drawer-key core/press-add tab))

(defevent ::toggle [db drawer-key]
  (change db drawer-key core/toggle))

(defevent ::close-search [db drawer-key]
  (change db drawer-key core/close-search))

(defevent ::fold-related [db drawer-key]
  (change db drawer-key core/fold-related))

(defevent ::ask-to-close-share [db drawer-key]
  (change db drawer-key core/ask-to-close-share))

(defevent-fx ::forget [{:keys [db]} drawer-key]
  {:db (update db ::drawers dissoc drawer-key)
   :fx [(stop-search-fx drawer-key :related-search)
        (stop-search-fx drawer-key :share-search)]})

;;
;; Its searches
;;

(defn- search-query
  "What the search under `search-key` asks for `text`. Related's looks through
   tasks, notes, contacts, tags and, with `spaces?`, spaces; Share's through
   contacts."
  [search-key text spaces?]
  (case search-key
    :related-search
    (cond-> [{(list :contact/search {:contact/search-text text})
              [:contact/uuid :contact/full-name :contact/email :contact/organisation?]}
             ;; A union query has to name every branch the collection can return.
             {(list :user/search {:search text})
              [{:collection/items {:note [:note/uuid :note/text]
                                   :task [:task/uuid :task/title]
                                   :prompt [:ai-prompt/title]
                                   :assistant [:ai-model-config/uuid]
                                   :mini-app [:mini-app/uuid]
                                   :contact [:contact/uuid]}}]}
             {(list :user/tags-search {:tag/search-text text})
              [:group/uuid :group/display-name]}]
      spaces?
      (conj {(list :spaces/table {:search text})
             [{:collection/items [:space/uuid :space/title]}]}))

    :share-search
    [{(list :contact/search {:contact/search-text text})
      contacts/query}]))

(defn- found-payload
  "What the search under `search-key` keeps of a `response`."
  [search-key response]
  (case search-key
    :related-search
    (let [items (get-in response [:user/search :collection/items])]
      {:hits {:task (filterv :task/uuid items)
              :note (filterv :note/uuid items)
              :contact (get response :contact/search [])
              :tag (get response :user/tags-search [])
              :space (get-in response [:spaces/table :collection/items] [])}})

    :share-search
    {:contacts (get response :contact/search [])}))

;; Typed text is dispatched synchronously (`>e!`) by the fields: a render ahead
;; of a queued keystroke would put older text back in the box.
(defevent-fx ::type-text
  [{:keys [db]} drawer-key search-key text spaces?]
  (let [db (change-search db drawer-key search-key core/type-text text)
        latest (get-in db [::drawers drawer-key search-key :latest])]
    {:db db
     :fx [(if (str/blank? latest)
            (stop-search-fx drawer-key search-key)
            [:debounce/dispatch {:id [::search drawer-key search-key]
                                 :event [::search drawer-key search-key latest spaces?]
                                 :duration search-debounce-ms}])]}))

;; Share's search takes text starting with @ as a domain, with no contacts to
;; look through.
(defevent-fx ::search
  [_ drawer-key search-key text spaces?]
  (if (and (= :share-search search-key) (str/starts-with? text "@"))
    {:fx [[:dispatch [::found drawer-key search-key text {}]]]}
    {:resolve {:query (search-query search-key text spaces?)
               :on-success-fx [[:dispatch [::found drawer-key search-key text '?body]]]}}))

(defevent ::found [db drawer-key search-key text response]
  (cond-> db
    (held? db drawer-key)
    (change-search drawer-key search-key core/found text (found-payload search-key response))))

(defevent-fx ::clear-text [{:keys [db]} drawer-key search-key]
  {:db (change-search db drawer-key search-key core/clear-text)
   :fx [(stop-search-fx drawer-key search-key)]})

;; A field going away leaves its search as it opened, as a field of its own
;; did.
(defevent-fx ::reset-search [{:keys [db]} drawer-key search-key]
  {:db (cond-> db
         (held? db drawer-key)
         (change-search drawer-key search-key (constantly (get (initial db drawer-key) search-key))))
   :fx [(stop-search-fx drawer-key search-key)]})

(defevent ::close-list [db drawer-key search-key]
  (change-search db drawer-key search-key core/close-list))

(defevent ::press-field [db drawer-key search-key]
  (change-search db drawer-key search-key core/press-field))

(defevent ::leave-field [db drawer-key search-key]
  (change-search db drawer-key search-key core/leave-field))

(defevent ::point [db drawer-key search-key index]
  (change-search db drawer-key search-key core/point index))

;;
;; The Share search
;;

(defevent-fx ::choose [{:keys [db]} drawer-key row]
  {:db (change-search db drawer-key :share-search core/choose row)
   :fx [(stop-search-fx drawer-key :share-search)]})

(defevent ::unpick [db drawer-key recipient-key]
  (change-search db drawer-key :share-search core/unpick recipient-key))

(defevent ::unpick-last [db drawer-key]
  (change-search db drawer-key :share-search core/unpick-last))

(defevent-fx ::clear [{:keys [db]} drawer-key]
  {:db (change-search db drawer-key :share-search core/clear)
   :fx [(stop-search-fx drawer-key :share-search)]})

(defevent ::set-permission [db drawer-key permission]
  (change-search db drawer-key :share-search core/set-permission permission))

(defevent ::keep-editing [db drawer-key]
  (change-search db drawer-key :share-search core/keep-editing))
